import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useRequest, useDebounce, useMemoizedFn } from 'ahooks';
import { Table, TableProps } from 'tea-component';
import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import CardSlot from '@gp/components/CardSlot';
import DataTime from '@gp/components/DataTime';
import Filter, { IStudioOverallManagementFilter } from './components/Filter';
import TableRow from './components/TableRow';
import TimeLine from './components/TimeLine';
import useInit from './hooks/useInit';
import useFilterOptions from './hooks/useFilterOptions';
import { reportOperationLog } from '@gp/services/global';
import {
  fetchStudioOverallManagementKeyIssuesData,
  fetchStudioOverallManagementBoardGovernanceData,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { lazyLoadingList, getValuesSpecial } from '@gp/utils/common';
import { getColumns, getShowList } from './utils/common';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { STUDIO_OVERALL_SCROLLABLE_EL_SELECTOR } from '@gp/pages/StudioDashboard/StudioOverall/constants';
import { MANAGEMENT_SEGMENT_KEY_ISSUES, MANAGEMENT_SEGMENT_BOARD_GOVERNANCE, LOG_MAP } from './constants';

import './index.scss';

const { autotip, injectable, mergeable } = Table.addons;

const segmentId: string = MANAGEMENT_SEGMENT_BOARD_GOVERNANCE;

const Management: React.FC<IStudioOverallManagementProps> = () => {
  const {
    loading: keyIssuesLoading,
    data: keyIssuesData,
    run: keyIssuesRun,
  } = useRequest(fetchStudioOverallManagementKeyIssuesData, { manual: true });

  const {
    loading: boardGovernanceLoading,
    data: boardGovernanceData,
    run: boardGovernanceRun,
  } = useRequest(fetchStudioOverallManagementBoardGovernanceData, {
    manual: true,
  });

  const { isOptionsDataLoading, optionsData } = useFilterOptions(segmentId);

  const keyIssuesTableLoading = useMemo<boolean>(
    () => isOptionsDataLoading || keyIssuesLoading,
    [isOptionsDataLoading, keyIssuesLoading],
  );

  const boardGovernanceListLoading = useMemo<boolean>(
    () => isOptionsDataLoading || boardGovernanceLoading,
    [isOptionsDataLoading, boardGovernanceLoading],
  );

  const boardGovernanceListIsNoData = useMemo<boolean>(() => {
    if (boardGovernanceLoading) return true;
    return !(Array.isArray(boardGovernanceData) && boardGovernanceData?.length);
  }, [boardGovernanceLoading, boardGovernanceData]);

  const [filter, setFilter] = useState<IStudioOverallManagementFilter>({
    studio: [],
    business_owner: [],
    year: '',
  });

  const [tableIsScroll, setTableIsScroll] = useState(false);
  const [dataList, setDataList] = useState<IStudioOverallManagementKeyIssuesResultData>([]);
  const [showDataList, setShowDataList] = useState<IStudioOverallManagementKeyIssuesResultData>([]);
  const [showList, setShowList] = useState<IStudioOverallManagementKeyIssuesData>([]);
  const [currentScrollTop, setCurrentScrollTop] = useState(0);
  const debounceCurrentScrollTop = useDebounce(currentScrollTop, { wait: 30 });
  // 滚动到最后时通知添加数据，记录滚动到的位置
  const [toAddList, setToAddList] = useState(0);
  // 记录每次添加数据时滚动到的位置，滚回来时去动态删除数据
  const addListScrollPosition = useRef<number[]>([]);
  const containerRef = useRef<any>(null);
  const [boardGovernanceListH, setBoardGovernanceListH] = useState(0);
  // 根据数据 计算合并单元格情况
  const [rowGroupNumList, setRowGroupNumList] = useState<Array<number>>([]);

  const keyIssuesRunPlus = (params: IStudioOverallManagementFilter) => {
    const paramsTemp: IStudioOverallManagementFilter = {
      ...params,
      studio: getValuesSpecial(params?.studio, optionsData?.studios),
      business_owner: getValuesSpecial(params?.business_owner, optionsData?.businesses),
    };
    keyIssuesRun(paramsTemp);
  };

  const boardGovernanceRunPlus = (params: IStudioOverallManagementFilter) => {
    const paramsTemp = {
      studio: getValuesSpecial(params?.studio, optionsData?.studios),
      business_owner: getValuesSpecial(params?.business_owner, optionsData?.businesses),
      date: params?.year || '',
    };
    boardGovernanceRun(paramsTemp);
  };

  const handleFilterChange = useMemoizedFn((payload: IStudioOverallManagementFilter, segmentId: string) => {
    const { studio, business_owner: businessOwners, year } = payload || {};
    const filterTemp = {
      ...filter,
      studio: Array.isArray(studio) ? studio : [],
      business_owner: Array.isArray(businessOwners) ? businessOwners : [],
      year,
    };
    setFilter(filterTemp);
    segmentId === MANAGEMENT_SEGMENT_KEY_ISSUES && keyIssuesRunPlus(filterTemp);
    segmentId === MANAGEMENT_SEGMENT_BOARD_GOVERNANCE && boardGovernanceRunPlus(filterTemp);
  });

  const handleScroll = useMemoizedFn((e?: any) => {
    const { scrollTop, scrollHeight, offsetHeight: currentHeight } = e?.target || {};
    // 滚动到最后
    if (scrollTop + currentHeight >= scrollHeight - 2) {
      // 允许2px误差
      setToAddList(scrollTop);
    }
    setCurrentScrollTop(scrollTop);
  });

  const handleResize = useMemoizedFn(() => {
    const tableBody = document.querySelector(STUDIO_OVERALL_SCROLLABLE_EL_SELECTOR);
    const rightContent = document.querySelector('.tea-layout__content');
    // @ts-ignore
    const flag = tableBody?.clientHeight < tableBody?.scrollHeight;
    setTableIsScroll(flag);

    // @ts-ignore
    const boardGovernanceListH = rightContent?.clientHeight - 160 - 75; // 160 = 20 + 34 + 20 + 50 + 16 + 20
    setBoardGovernanceListH(boardGovernanceListH);
  });

  useEffect(() => {
    if (Array.isArray(showList) && showList?.length) {
      const tempList = [];
      let preRowId = 0;
      let rowNum = 0;
      for (let i = 0; i < showList?.length; i++) {
        const t = showList?.[i];
        if (!t?.isMergeable) {
          rowNum = i - preRowId;
          preRowId = i;
          tempList.push(rowNum);
        }
      }
      tempList.push(showList?.length - preRowId);
      tempList.shift();
      setRowGroupNumList(tempList);
    }
  }, [showList]);

  let groupIndex = 0;

  const addons = [
    injectable({
      row: (props, context) => ({
        className: !context.record?.isMergeable && context.recordIndex !== 0 ? 'table-group-row' : '',
      }),
    }),
    mergeable({
      rowSpan: (columnIndex, recordIndex, context) => {
        if (columnIndex === 0) {
          if (!context.record?.isMergeable && groupIndex < rowGroupNumList.length) {
            groupIndex += 1;
            return rowGroupNumList[groupIndex - 1];
          }
          return 0;
        }
        return 1;
      },
    }),
    autotip({
      isLoading: keyIssuesTableLoading,
      hideIcon: !keyIssuesTableLoading, // loading状态显示图标，其他情况不显示
      emptyText: <span style={{ color: '#444' }}>No Data</span>,
      style: {
        height: 200,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        margin: '0 auto',
      },
    }),
  ];

  // 计算表格最大高度及是否出现滚动条
  useEffect(() => {
    if (keyIssuesTableLoading) return;
    setTimeout(() => {
      const tableBody = document.querySelector(STUDIO_OVERALL_SCROLLABLE_EL_SELECTOR);
      // @ts-ignore
      const flag = tableBody?.clientHeight < tableBody?.scrollHeight;
      setTableIsScroll(flag);
    });
  }, [keyIssuesTableLoading]);

  useInit({
    segmentId,
    isOptionsDataLoading,
    optionsData,
    setFilter,
    keyIssuesRunPlus,
    boardGovernanceRunPlus,
  });

  // 初始化显示数据
  useEffect(() => {
    const initShowList = lazyLoadingList(dataList, [], 10, 1, 10);
    setShowDataList(initShowList);
    setShowList(getShowList(initShowList));
  }, [dataList]);

  // 滚动到最后时，加数据
  useEffect(() => {
    if (showDataList?.length >= dataList?.length || !toAddList) return;
    const newShowList = lazyLoadingList(dataList, showDataList, 10, 1, 10);
    setShowDataList(newShowList);
    setShowList(getShowList(newShowList));
    addListScrollPosition.current = [...addListScrollPosition.current, toAddList];
  }, [toAddList]);

  // 滚动条往回滚时，减数据
  useEffect(() => {
    if (Array.isArray(addListScrollPosition?.current) && addListScrollPosition?.current?.length) {
      const tempAddListScrollPosition = [...addListScrollPosition?.current];
      tempAddListScrollPosition?.sort((a, b) => a - b);
      let delCount = 0;
      let delStartPosition: number | null = null; // 删除的个数及删除起始位置
      for (let i = 0; i < tempAddListScrollPosition?.length; i++) {
        const t = tempAddListScrollPosition?.[i];
        if (t > debounceCurrentScrollTop) {
          delStartPosition = i;
          delCount = tempAddListScrollPosition?.length - i;
          break;
        }
      }
      // 删除之前的数据
      if (delCount) {
        if (delStartPosition && delStartPosition > 0) {
          // 正常删除
          addListScrollPosition.current = tempAddListScrollPosition?.slice(0, delStartPosition);
          const newShowList = lazyLoadingList(dataList, showDataList, 10, 2, 10 * delCount);
          setShowDataList(newShowList);
          setShowList(getShowList(newShowList));
        } else if (delStartPosition === 0) {
          // 全部删除添加的数据，只剩初始数据
          addListScrollPosition.current = [];
          const newShowList = lazyLoadingList(dataList, [], 10, 1, 10);
          setShowDataList(newShowList);
          setShowList(getShowList(newShowList));
        } else {
          // 不用删除
        }
        setToAddList(0);
      }
    }
  }, [debounceCurrentScrollTop]);

  useEffect(() => {
    if (keyIssuesLoading) {
      setDataList([]);
      return;
    }
    const { issue_list: issueList } = keyIssuesData || {};
    const hasData = Array.isArray(issueList) && issueList.length > 0;
    setDataList(hasData ? [...issueList] : []);
  }, [keyIssuesLoading]);

  // 初始化相关事件监听
  useEffect(() => {
    if (keyIssuesLoading) return;

    const t = setInterval(() => {
      const tableBody = document.querySelector(STUDIO_OVERALL_SCROLLABLE_EL_SELECTOR);
      if (tableBody) {
        containerRef.current = tableBody;
        tableBody?.addEventListener('scroll', handleScroll);
        clearInterval(t);
      }
    }, 100);

    return () => {
      containerRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [keyIssuesLoading]);

  // 取得Board Governance列表占的最大高度
  useEffect(() => {
    const t = setInterval(() => {
      const rightContent = document.querySelector('.tea-layout__content');
      if (rightContent) {
        // @ts-ignore
        const boardGovernanceListH = rightContent?.clientHeight - 160 - 75; // 160 = 20 + 34 + 20 + 50 + 16 + 20
        setBoardGovernanceListH(boardGovernanceListH);
        clearInterval(t);
      }
    }, 100);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 打点
  useEffect(() => {
    if (!segmentId) return;
    // @ts-ignore
    reportOperationLog(ALL_REPORT_OPERATION_LOG[LOG_MAP[segmentId]]);
  }, [segmentId]);

  return (
    <div className="dbg-studio-overall-management">
      <div className="gp-studio-overall-management-header">
        <Filter
          segmentId={segmentId}
          loading={isOptionsDataLoading}
          optionsData={optionsData}
          value={filter}
          onChange={handleFilterChange}
        />
        {segmentId === MANAGEMENT_SEGMENT_KEY_ISSUES && (
          <DataTime
            className="dbg-studio-overall-management-key-issues-update-time"
            dateType="Month"
            date={keyIssuesData?.update_time || ''}
          />
        )}
      </div>

      {segmentId === MANAGEMENT_SEGMENT_KEY_ISSUES && (
        <CardSlot className="dbg-studio-overall-management-table">
          <Table
            className={classnames(
              'dbg-common-table dbg-table-sticky management-key-issues-list-table font14 dbh-action-state-center-table',
              {
                'td-table-layout': keyIssuesTableLoading || !showList?.length,
                'scroll-table-header': tableIsScroll,
              },
            )}
            addons={addons}
            recordKey="uniqueId"
            records={keyIssuesTableLoading ? [] : showList}
            columns={getColumns() as TableProps['columns']}
          />
        </CardSlot>
      )}
      {segmentId === MANAGEMENT_SEGMENT_BOARD_GOVERNANCE && (
        <div className="dbg-studio-overall-management-board-governance-container">
          {boardGovernanceListLoading && (
            <Loading
              style={{ height: boardGovernanceListH + 66, marginTop: 20 }}
              className="font14"
              loading
              loadingBg="transparent"
            />
          )}
          {!boardGovernanceListLoading && boardGovernanceListIsNoData && (
            <NoData height={boardGovernanceListH + 66} constomStyle={{ fontSize: 14, marginTop: 20 }} />
          )}
          {!boardGovernanceListLoading && !boardGovernanceListIsNoData && (
            <>
              <TimeLine style={{ marginTop: 20, marginBottom: 16 }} year={filter?.year} />
              <div className="dbg-studio-overall-management-board-governance-box">
                <div className="dbg-studio-overall-management-board-governance-inner-box">
                  {boardGovernanceData?.map((t) => (
                    <TableRow key={t?.studio_id} data={t} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(Management);
